import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, message } from 'antd';
import { CheckOutlined, IdcardOutlined } from '@ant-design/icons';
import { ChatPanel, type ChatMessage, type ChatParticipant, type ChatStatusLabels } from '../../../components/chat';
import { useTranslation } from '../../../i18n';
import {
  getEmailConversationDetail,
  getEmailConversations,
  getEmailPendingReplyCount,
  getEmailStats,
  markEmailConversationRead,
  replyEmailConversation
} from '../../../store/request';
import { useStore } from '../../../store/useStore';
import { userInitial } from '../../../utils/userInitial';
import type {
  EmailConversationDetail,
  EmailConversationListItem,
  EmailConversationMessage,
  EmailInboxStats,
  EmailNewMessageEvent
} from '../../../types/email';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useMerchantRealtime } from '../layout/merchantRealtimeContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import { BindEmailModal } from './BindEmailModal';
import { EmailConversationList, type EmailListFilter, type EmailListRow } from './EmailConversationList';
import { emailStatusTag } from './emailTags';
import { NewEmailModal } from './NewEmailModal';

const EMAIL_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 50;

function plainEmailBody(html?: string) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapEmailMessage(item: EmailConversationMessage, index: number): ChatMessage {
  const outbound = item.direction === 'OUTBOUND';
  const text = item.bodyText?.trim() || plainEmailBody(item.bodyHtml);
  return {
    id: item.id != null ? String(item.id) : `mail-${index}`,
    direction: outbound ? 'out' : 'in',
    contentType: 'text',
    subject: item.subject?.trim() || undefined,
    content: text || '—',
    createdAt: item.occurredAt || '',
    status: outbound ? (item.opened ? 'read' : 'delivered') : undefined
  };
}

type EmailThread = {
  id: string;
  subject: string;
  handle: string;
  email: string;
  region: string;
  category: string;
  influencerId?: string;
  creatorOpenId?: string;
  status?: string;
  unreadCount?: number;
  sentCount?: number;
  receivedCount?: number;
  openedCount?: number;
  needReply: boolean;
  messages: ChatMessage[];
  earliestPage: number;
  pageSize: number;
  peer: ChatParticipant;
};

function mapEmailThread(
  id: string,
  detail: EmailConversationDetail,
  page: number,
  pageSize: number
): EmailThread {
  const contact = detail.contact;
  const stats = detail.stats;
  const name = contact?.name?.trim() || contact?.handle?.trim() || contact?.email?.trim() || '—';
  return {
    id,
    subject: detail.subject?.trim() || '',
    handle: contact?.handle?.trim() || '',
    email: contact?.email?.trim() || '',
    region: contact?.region?.trim() || '',
    category: contact?.category?.trim() || '',
    influencerId: [detail.influencerId, contact?.influencerId]
      .map((value) => (value == null ? '' : String(value).trim()))
      .find((value) => value.length > 0),
    creatorOpenId: contact?.creatorOpenId?.trim() || undefined,
    status: stats?.status,
    unreadCount: stats?.unreadCount,
    sentCount: stats?.sentCount,
    receivedCount: stats?.receivedCount,
    openedCount: stats?.openedCount,
    needReply: stats?.needReply === true,
    messages: (detail.messages ?? []).map(mapEmailMessage),
    earliestPage: detail.messagePageNum ?? page,
    pageSize: detail.messagePageSize ?? pageSize,
    peer: {
      name,
      initial: name === '—' ? '?' : name.slice(0, 1).toUpperCase(),
      color: '#596ae8',
      avatarUrl: contact?.avatarUrl?.trim() || undefined
    }
  };
}

function formatStat(value?: number) {
  return typeof value === 'number' ? value.toLocaleString('en-US') : '—';
}

function mapEmailConversation(item: EmailConversationListItem, index: number): EmailListRow {
  const name = item.contactName?.trim() || item.handle?.trim() || item.contactEmail?.trim() || '—';
  return {
    id: item.id != null ? String(item.id) : `email-${index}`,
    name,
    email: item.contactEmail?.trim() || '—',
    handle: item.handle?.trim() || '—',
    avatarUrl: item.avatarUrl?.trim() || undefined,
    subject: item.subject?.trim() || '—',
    status: item.status,
    unreadCount: item.unreadCount ?? 0,
    preview: item.lastMessagePreview?.trim() || '—',
    time: item.lastMessageTime,
    direction: item.lastDirection,
    sentCount: item.sentCount ?? 0,
    peer: {
      name,
      initial: name === '—' ? '?' : name.slice(0, 1).toUpperCase(),
      color: '#596ae8',
      avatarUrl: item.avatarUrl?.trim() || undefined
    }
  };
}

function StatCard({
  label,
  value,
  hint,
  hintClassName = 'text-slate-400',
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  hintClassName?: string;
}) {
  return (
    <div className="rounded-[14px] bg-white px-4 py-3 shadow-[0_1px_3px_rgba(3,0,71,0.09)]">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 truncate text-xl font-semibold text-slate-800">{value}</div>
      {hint ? <div className={`mt-0.5 truncate text-xs ${hintClassName}`}>{hint}</div> : null}
    </div>
  );
}

function Chip({ children, className = 'bg-slate-100 text-slate-500' }: { children: React.ReactNode; className?: string }) {
  return <span className={`rounded px-2 text-[11px] leading-5 ${className}`}>{children}</span>;
}

export const Email = observer(() => {
  const { t } = useTranslation();
  const { emailStore: store, loginStore } = useStore();
  const { shopId } = useSelectedShop();
  const currentAccount = useCurrentAccount();
  const platformEmail = currentAccount?.platformEmail?.trim() || '';
  const [newOpen, setNewOpen] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [queryKeyword, setQueryKeyword] = useState('');
  const [filter, setFilter] = useState<EmailListFilter>('all');
  const [pageNum, setPageNum] = useState(1);
  const [rows, setRows] = useState<EmailListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeId, setActiveId] = useState<string>();
  const [pendingReplyCount, setPendingReplyCount] = useState<number | null>(null);
  const [inboxStats, setInboxStats] = useState<EmailInboxStats | null>(null);
  const [thread, setThread] = useState<EmailThread | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [replying, setReplying] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [mailReload, setMailReload] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const [trackedShopId, setTrackedShopId] = useState(shopId);
  const listEpoch = useRef(0);
  const detailGenRef = useRef(0);
  const listBusyRef = useRef(false);
  const detailBusyRef = useRef(false);
  const queuedEmailRef = useRef<EmailNewMessageEvent[]>([]);
  const flushEmailRef = useRef<() => void>(() => undefined);
  const shopIdRef = useRef(shopId);
  const filterRef = useRef(filter);
  const activeIdRef = useRef(activeId);
  const rowsRef = useRef(rows);
  const threadRef = useRef(thread);
  const statsLockRef = useRef({ running: false, again: false });

  shopIdRef.current = shopId;
  filterRef.current = filter;
  activeIdRef.current = activeId;
  rowsRef.current = rows;
  threadRef.current = thread;
  if (trackedShopId !== shopId) {
    listBusyRef.current = true;
    threadRef.current = null;
    setTrackedShopId(shopId);
    setActiveId(undefined);
    setThread(null);
  }

  const selfName = currentAccount?.displayName?.trim() || loginStore.data.userinfo?.userName?.trim() || '';
  const selfParticipant = useMemo<ChatParticipant>(
    () => ({ name: t('chat.me'), initial: userInitial(selfName), color: '#596ae8' }),
    [selfName, t],
  );

  const emailStatusLabels = useMemo<ChatStatusLabels>(
    () => ({ delivered: t('chat.delivered'), read: t('email.opened') }),
    [t],
  );

  const account = store.account;
  const conversation = rows.find((item) => item.id === activeId);

  useEffect(() => {
    const timer = window.setTimeout(() => setQueryKeyword(keyword.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    if (!shopId) {
      setPendingReplyCount(null);
      return;
    }
    let cancelled = false;
    getEmailPendingReplyCount({ shopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || typeof res.content !== 'number') {
          if (res.code !== -1) message.error(res.message || t('email.pending_reply_failed'));
          setPendingReplyCount(null);
          return;
        }
        setPendingReplyCount(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('email.pending_reply_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, mailReload, t]);

  useEffect(() => {
    if (!shopId) {
      setInboxStats(null);
      return;
    }
    let cancelled = false;
    getEmailStats({ shopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('email.stats_load_failed'));
          setInboxStats(null);
          return;
        }
        setInboxStats(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('email.stats_load_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, mailReload, t]);

  useEffect(() => {
    const epoch = listEpoch.current + 1;
    listEpoch.current = epoch;
    if (!shopId) {
      rowsRef.current = [];
      listBusyRef.current = false;
      setRows([]);
      setTotal(0);
      setHasMore(false);
      setPageNum(1);
      setListLoading(false);
      setLoadingMore(false);
      flushEmailRef.current();
      return;
    }
    let cancelled = false;
    listBusyRef.current = true;
    rowsRef.current = [];
    setListLoading(true);
    setLoadingMore(false);
    setRows([]);
    setTotal(0);
    setHasMore(false);
    setPageNum(1);
    getEmailConversations({
      shopId,
      keyword: queryKeyword || undefined,
      status: filter === 'all' ? undefined : filter,
      pageNum: 1,
      pageSize: EMAIL_PAGE_SIZE
    })
      .then((res) => {
        if (cancelled || listEpoch.current !== epoch) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('email.load_failed'));
          rowsRef.current = [];
          setRows([]);
          setTotal(0);
          setHasMore(false);
          return;
        }
        const list = (res.content.conversations ?? []).map(mapEmailConversation);
        const nextTotal = res.content.total ?? list.length;
        rowsRef.current = list;
        setRows(list);
        setTotal(nextTotal);
        setHasMore(list.length < nextTotal);
      })
      .catch(() => {
        if (!cancelled && listEpoch.current === epoch) message.error(t('email.load_failed'));
      })
      .finally(() => {
        if (cancelled || listEpoch.current !== epoch) return;
        listBusyRef.current = false;
        setListLoading(false);
        flushEmailRef.current();
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, filter, queryKeyword, mailReload, listVersion, t]);

  const loadMoreConversations = useCallback(() => {
    if (!shopId || !hasMore || listLoading || loadingMore || listBusyRef.current) return;
    const epoch = listEpoch.current;
    const nextPage = pageNum + 1;
    listBusyRef.current = true;
    setLoadingMore(true);
    getEmailConversations({
      shopId,
      keyword: queryKeyword || undefined,
      status: filter === 'all' ? undefined : filter,
      pageNum: nextPage,
      pageSize: EMAIL_PAGE_SIZE
    })
      .then((res) => {
        if (listEpoch.current !== epoch) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('email.load_failed'));
          return;
        }
        const list = (res.content.conversations ?? []).map(mapEmailConversation);
        const nextTotal = typeof res.content.total === 'number' ? res.content.total : total;
        const seen = new Set(rowsRef.current.map((item) => item.id));
        const merged = [...rowsRef.current, ...list.filter((item) => !seen.has(item.id))];
        rowsRef.current = merged;
        setRows(merged);
        setTotal(nextTotal);
        setPageNum(nextPage);
        setHasMore(merged.length < nextTotal && list.length > 0);
      })
      .catch(() => {
        if (listEpoch.current === epoch) message.error(t('email.load_failed'));
      })
      .finally(() => {
        if (listEpoch.current !== epoch) return;
        listBusyRef.current = false;
        setLoadingMore(false);
        flushEmailRef.current();
      });
  }, [shopId, hasMore, listLoading, loadingMore, pageNum, queryKeyword, filter, total, t]);

  useEffect(() => {
    const gen = detailGenRef.current + 1;
    detailGenRef.current = gen;
    const finish = () => {
      if (detailGenRef.current !== gen) return;
      detailBusyRef.current = false;
      setDetailLoading(false);
      flushEmailRef.current();
    };
    if (!shopId || !activeId || !/^\d+$/.test(activeId)) {
      threadRef.current = null;
      setThread(null);
      finish();
      return;
    }
    detailBusyRef.current = true;
    let cancelled = false;
    setThread((current) => {
      if (current?.id === activeId) return current;
      threadRef.current = null;
      return null;
    });
    setDetailLoading(true);
    const load = async () => {
      const first = await getEmailConversationDetail(activeId, {
        shopId,
        pageNum: 1,
        pageSize: MESSAGE_PAGE_SIZE
      });
      if (cancelled || detailGenRef.current !== gen) return;
      if (first.code !== 200 || !first.content) {
        if (first.code !== -1) message.error(first.message || t('email.thread_load_failed'));
        return;
      }
      const pageSize = first.content.messagePageSize ?? MESSAGE_PAGE_SIZE;
      const messageTotal = first.content.messageTotal ?? first.content.messages?.length ?? 0;
      const lastPage = Math.max(1, Math.ceil(messageTotal / pageSize));
      let content = first.content;
      let page = 1;
      if (lastPage > 1) {
        const last = await getEmailConversationDetail(activeId, {
          shopId,
          pageNum: lastPage,
          pageSize
        });
        if (cancelled || detailGenRef.current !== gen) return;
        if (last.code === 200 && last.content) {
          content = last.content;
          page = lastPage;
        }
      }
      const nextThread = mapEmailThread(activeId, content, page, pageSize);
      threadRef.current = nextThread;
      setThread(nextThread);
      const nextRows = rowsRef.current.map((row) =>
        row.id === activeId
          ? {
            ...row,
            status: nextThread.status ?? row.status,
            unreadCount: nextThread.unreadCount ?? row.unreadCount,
            sentCount: nextThread.sentCount ?? row.sentCount
          }
          : row
      );
      rowsRef.current = nextRows;
      setRows(nextRows);
    };
    load()
      .catch(() => {
        if (!cancelled && detailGenRef.current === gen) message.error(t('email.thread_load_failed'));
      })
      .finally(finish);
    return () => {
      cancelled = true;
      detailGenRef.current += 1;
    };
  }, [activeId, shopId, mailReload, t]);

  const scheduleEmailStats = () => {
    const lock = statsLockRef.current;
    const run = () => {
      const currentShop = shopIdRef.current;
      if (!currentShop) return;
      if (lock.running) {
        lock.again = true;
        return;
      }
      lock.running = true;
      Promise.all([getEmailPendingReplyCount({ shopId: currentShop }), getEmailStats({ shopId: currentShop })])
        .then(([pendingRes, statsRes]) => {
          if (shopIdRef.current !== currentShop) return;
          if (pendingRes.code === 200 && typeof pendingRes.content === 'number') {
            setPendingReplyCount(pendingRes.content);
          }
          if (statsRes.code === 200 && statsRes.content) setInboxStats(statsRes.content);
        })
        .catch(() => undefined)
        .finally(() => {
          lock.running = false;
          if (lock.again) {
            lock.again = false;
            run();
          }
        });
    };
    run();
  };

  const applyEmailPush = (event: EmailNewMessageEvent) => {
    if (event.message?.direction?.trim().toUpperCase() !== 'INBOUND') return;
    const rawId = event.conversationId ?? event.conversation?.id;
    const id = rawId != null ? String(rawId) : '';
    if (!id || !shopIdRef.current) return;
    const nextStatus = event.conversation?.status?.trim();
    const unread = event.conversation?.unreadCount;
    const preview = event.conversation?.lastMessagePreview?.trim() || event.message?.bodyText?.trim() || '';
    const receivedAt = new Date().toISOString();
    const filterNow = filterRef.current;
    const filterRejects = filterNow !== 'all' && !!nextStatus && filterNow !== nextStatus;

    if (activeIdRef.current === id) {
      setThread((prev) => {
        if (!prev || prev.id !== id) return prev;
        const messageId = event.message?.id != null ? String(event.message.id) : '';
        const exists = messageId ? prev.messages.some((item) => item.id === messageId) : false;
        const incoming: ChatMessage = {
          id: messageId || `mail-ws-${receivedAt}`,
          direction: 'in',
          contentType: 'text',
          subject: event.message?.subject?.trim() || undefined,
          content: event.message?.bodyText?.trim() || '—',
          createdAt: receivedAt
        };
        const next = {
          ...prev,
          status: nextStatus || prev.status,
          unreadCount: typeof unread === 'number' ? unread : prev.unreadCount,
          needReply: nextStatus ? nextStatus === 'NEED_REPLY' : true,
          receivedCount: exists ? prev.receivedCount : (prev.receivedCount ?? 0) + 1,
          messages: exists ? prev.messages : [...prev.messages, incoming]
        };
        threadRef.current = next;
        return next;
      });
    }

    const inList = rowsRef.current.some((row) => row.id === id);
    if (inList && filterRejects) {
      const nextRows = rowsRef.current.filter((row) => row.id !== id);
      rowsRef.current = nextRows;
      setRows(nextRows);
      setTotal((value) => Math.max(0, value - 1));
    } else if (inList) {
      const current = rowsRef.current.find((row) => row.id === id);
      if (current) {
        const nextRow: EmailListRow = {
          ...current,
          status: nextStatus || current.status,
          unreadCount: typeof unread === 'number' ? unread : current.unreadCount,
          preview: preview || current.preview,
          time: receivedAt,
          direction: 'INBOUND'
        };
        const nextRows = [nextRow, ...rowsRef.current.filter((row) => row.id !== id)];
        rowsRef.current = nextRows;
        setRows(nextRows);
      }
    } else if (!filterRejects && !listBusyRef.current) {
      listBusyRef.current = true;
      setListVersion((value) => value + 1);
    }
    scheduleEmailStats();
  };

  const enqueueEmailPush = (event: EmailNewMessageEvent) => {
    if (listBusyRef.current || detailBusyRef.current) {
      queuedEmailRef.current.push(event);
      return;
    }
    applyEmailPush(event);
  };

  flushEmailRef.current = () => {
    if (listBusyRef.current || detailBusyRef.current) return;
    const queued = queuedEmailRef.current.splice(0);
    queued.forEach((event) => applyEmailPush(event));
  };

  useMerchantRealtime((event) => {
    if (event.event === 'EMAIL_SOCKET_RECONNECTED') {
      if (!shopIdRef.current) return;
      listBusyRef.current = true;
      if (activeIdRef.current && /^\d+$/.test(activeIdRef.current)) detailBusyRef.current = true;
      setMailReload((value) => value + 1);
      return;
    }
    if (event.event !== 'EMAIL_NEW_MESSAGE') return;
    enqueueEmailPush(event as EmailNewMessageEvent);
  });

  const copyAddress = async () => {
    if (!platformEmail) {
      return;
    }
    try {
      await navigator.clipboard.writeText(platformEmail);
      message.success(t('email.copied', { address: platformEmail }));
    } catch {
      message.info(t('email.platform_fallback', { address: platformEmail }));
    }
  };

  const syncTalents = async () => {
    setSyncing(true);
    try {
      const count = await store.syncTalents();
      message.success(t('email.synced', { count }));
    } finally {
      setSyncing(false);
    }
  };

  const shown = thread && thread.id === activeId ? thread : null;
  const peerSource = shown?.peer ?? conversation?.peer;
  const peer = peerSource
    ? { ...peerSource, avatarUrl: peerSource.avatarUrl || conversation?.peer.avatarUrl }
    : undefined;
  const threadStatus = shown?.status ?? conversation?.status;
  const statusTag = emailStatusTag(threadStatus);
  const subtitleParts = shown
    ? [shown.handle, shown.region, shown.category, shown.email].filter(Boolean)
    : [conversation?.handle, conversation?.email].filter((item) => item && item !== '—');
  const subjectText = shown?.subject || (conversation && conversation.subject !== '—' ? conversation.subject : '');

  const loadEarlierMessages = () => {
    if (!shopId || !shown || shown.earliestPage <= 1 || detailLoading || loadingEarlier) return;
    const page = shown.earliestPage - 1;
    setLoadingEarlier(true);
    getEmailConversationDetail(shown.id, {
      shopId,
      pageNum: page,
      pageSize: shown.pageSize
    })
      .then((res) => {
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('email.thread_load_failed'));
          return;
        }
        const older = (res.content.messages ?? []).map(mapEmailMessage);
        setThread((current) => {
          if (!current || current.id !== shown.id) return current;
          return {
            ...current,
            messages: [...older, ...current.messages],
            earliestPage: res.content?.messagePageNum ?? page
          };
        });
      })
      .catch(() => message.error(t('email.thread_load_failed')))
      .finally(() => setLoadingEarlier(false));
  };

  const bounced = (shown?.status ?? conversation?.status) === 'HARD_BOUNCE';

  const sendReply = async (text: string) => {
    const content = text.trim();
    if (!activeId || !/^\d+$/.test(activeId) || !content) {
      throw new Error('empty');
    }
    if (bounced) {
      message.warning(t('email.blocked_bounced'));
      throw new Error('bounced');
    }
    setReplying(true);
    try {
      const res = await replyEmailConversation(activeId, { text: content });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('email.reply_failed'));
        throw new Error('failed');
      }
      message.success(res.message || t('email.reply_sent'));
      setMailReload((value) => value + 1);
    } catch (error) {
      if (error instanceof Error && (error.message === 'empty' || error.message === 'bounced' || error.message === 'failed')) {
        throw error;
      }
      message.error(t('email.reply_failed'));
      throw error;
    } finally {
      setReplying(false);
    }
  };

  const unreadCount = shown ? shown.unreadCount ?? 0 : conversation?.unreadCount ?? 0;

  const markRead = async () => {
    if (!activeId || !/^\d+$/.test(activeId) || markingRead) return;
    setMarkingRead(true);
    try {
      const res = await markEmailConversationRead(activeId);
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('email.mark_read_failed'));
        return;
      }
      message.success(res.message || t('email.marked_read'));
      setMailReload((value) => value + 1);
    } catch {
      message.error(t('email.mark_read_failed'));
    } finally {
      setMarkingRead(false);
    }
  };

  const headerSubtitle = peer ? (
    <span>
      {subtitleParts.join(' · ')}
      {subjectText ? `${subtitleParts.length ? ' · ' : ''}${subjectText}` : ''}
    </span>
  ) : null;

  const headerActions = peer ? (
    shown?.influencerId ? (
      <Button
        size="small"
        icon={<IdcardOutlined />}
        className="chat-header-action rounded-md"
        href={`/creator/talent_square/${encodeURIComponent(shown.influencerId)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('email.talent_detail')}
      </Button>
    ) : (
      <Button
        size="small"
        icon={<IdcardOutlined />}
        className="chat-header-action rounded-md"
        onClick={() => message.warning(t('message.profile_missing'))}
      >
        {t('email.talent_detail')}
      </Button>
    )
  ) : null;

  const threadToolbar = peer ? (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#eef0f4] px-5 py-2">
      {statusTag ? <Chip className={statusTag.className}>{statusTag.label}</Chip> : null}
      {shown?.needReply && threadStatus !== 'NEED_REPLY' ? (
        <Chip className="bg-amber-50 text-amber-600">{t('email.pending')}</Chip>
      ) : null}
      {typeof shown?.unreadCount === 'number' && shown.unreadCount > 0 ? (
        <Chip className="bg-[#596ae8]/10 text-[#596ae8]">
          {t('email.unread_count', { count: shown.unreadCount })}
        </Chip>
      ) : null}
      {typeof shown?.sentCount === 'number' ? <Chip>{t('email.sent_count', { count: shown.sentCount })}</Chip> : null}
      {typeof shown?.receivedCount === 'number' ? (
        <Chip>{t('email.received_count', { count: shown.receivedCount })}</Chip>
      ) : null}
      {/* {typeof shown?.openedCount === 'number' ? (
        <Chip>{t('email.opened_count', { count: shown.openedCount })}</Chip>
      ) : null} */}
    </div>
  ) : null;

  return (
    <div className="container flex flex-col !overflow-hidden">
      <header className="mb-4 flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">{t('email.title')}</h1>
      </header>

      <div className="grid shrink-0 grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          label={t('email.platform_email')}
          value={<span className="text-base">{platformEmail || '—'}</span>}
          hint={account?.boundAddress ? t('email.forward_hint', { address: account.boundAddress }) : t('email.domain_hint')}
        />
        <StatCard label={t('email.sent_month')} value={formatStat(inboxStats?.monthlySentCount)} />
        <StatCard label={t('email.unread')} value={formatStat(inboxStats?.unreadCount)} />
        <StatCard
          label={t('email.pending')}
          value={formatStat(inboxStats?.pendingReplyCount)}
          hint={
            typeof inboxStats?.pendingReplyCount === 'number' && inboxStats.pendingReplyCount > 0
              ? t('email.pending_reply_hint')
              : undefined
          }
          hintClassName="text-amber-600"
        />
      </div>

      <div className="my-3 flex shrink-0 flex-wrap items-center gap-2">
        <Button type="primary" className="wb-btn-primary rounded-lg" onClick={() => setNewOpen(true)}>
          {t('email.new_send')}
        </Button>
        {/* <Button className="rounded-lg" loading={syncing} onClick={syncTalents}>
          {t('email.sync_talents')}
        </Button> */}
        {/* <Button className="rounded-lg" onClick={() => setBindOpen(true)}>
          {account?.boundAddress ? t('email.change_bind') : t('email.bind_mine')}
        </Button> */}
        <Button className="rounded-lg" onClick={copyAddress}>
          {t('email.copy_platform')}
        </Button>
      </div>

      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_1px_3px_rgba(3,0,71,0.09)]">
        <div className="flex shrink-0 items-center justify-between border-b border-[#eef0f4] px-5 py-3">
          <span className="text-[15px] font-semibold text-slate-800">{t('email.conversations')}</span>
          <span className="text-xs text-slate-400">
            {t('email.conversation_total', { total })}
          </span>
        </div>
        <div className="flex min-h-0 flex-1">
          <div className="w-[320px] shrink-0 border-r border-[#eef0f4]">
            <EmailConversationList
              conversations={rows}
              activeId={activeId}
              filter={filter}
              onFilterChange={setFilter}
              keyword={keyword}
              onKeywordChange={setKeyword}
              loading={listLoading}
              loadingMore={loadingMore}
              emptyText={keyword.trim() ? t('email.no_match') : t('email.empty_conversations')}
              pendingReplyCount={pendingReplyCount}
              onLoadMore={loadMoreConversations}
              onSelect={setActiveId}
            />
          </div>
          <main className="min-w-0 flex-1">
            <ChatPanel
              conversationKey={activeId}
              peer={peer}
              self={selfParticipant}
              messages={shown?.messages ?? []}
              headerSubtitle={headerSubtitle}
              headerActions={headerActions}
              toolbar={threadToolbar}
              hasMore={Boolean(shown && shown.earliestPage > 1)}
              loadingEarlier={detailLoading || loadingEarlier}
              onLoadEarlier={loadEarlierMessages}
              onSend={sendReply}
              sending={replying}
              sendDisabledReason={bounced ? t('email.blocked_bounced') : undefined}
              statusLabels={emailStatusLabels}
              composerPlaceholder={peer ? t('email.reply_placeholder', { name: peer.name }) : undefined}
              sendLabel={t('email.reply')}
              submitOn="ctrlNewline"
              composerLayout="stacked"
              composerExtraActions={
                unreadCount > 0 ? (
                  <Button
                    type="text"
                    icon={<CheckOutlined />}
                    loading={markingRead}
                    className="chat-header-action rounded-lg text-slate-500"
                    onClick={markRead}
                  >
                    {t('email.mark_read')}
                  </Button>
                ) : null
              }
              idleText={t('email.idle')}
              emptyText={t('email.empty_thread')}
            />
          </main>
        </div>
      </section>

      <NewEmailModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSent={() => setMailReload((value) => value + 1)}
      />
      <BindEmailModal
        open={bindOpen}
        currentAddress={account?.boundAddress}
        onClose={() => setBindOpen(false)}
        onBind={store.bindAddress}
      />
    </div>
  );
});
